/** Russkij kontent. Nazvaniya rezhimov sleduyut katalogu `ru.json`. */
import type { LanguageContent } from '../registry.js';

export const RU: LanguageContent = {
  home: {
    intro: [
      'Retusch это онлайн-игра в рисование для 2-10 игроков, она работает в браузере, без установки и без аккаунта. Каждый раунд вам достаётся картина, с которой стёрли один участок: перерисуйте его так, чтобы он исчез внутри произведения. Потом роли меняются, и приходит черёд выискивать ретуши остальных.',
      'Почти все игры в рисование награждают того, кого узнали первым. Retusch требует обратного: побеждает тот, кого не заметили.',
    ],
    steps: [
      {
        title: 'Картина с дырой',
        text: 'Вам достаётся иллюстрация, с которой стёрли один участок.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Вы его перерисовываете',
        text: 'Вы дорисовываете участок так, чтобы он слился с произведением.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'Охота',
        text: 'Картина возвращается с ретушами всех игроков: найдите их раньше остальных.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'Раскрытие',
        text: 'Каждый тайник выходит наружу, и падают очки.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Общая охота',
        text: 'Все рисуют на одной и той же картине, каждый на своём участке. Потом охота открывается сразу для всех: ретушь находят только один раз, и это делает тот, кто заметил её первым. Это режим по умолчанию, тот, в котором всё время что-то происходит.',
      },
      {
        name: 'Стрим',
        text: 'Хозяин комнаты не рисует: он ищет один, пока все остальные прячутся в его картине. Придуман для прямого эфира, где сама охота и есть зрелище. Бесплатно навсегда.',
      },
      {
        name: 'У каждого своя картина',
        text: 'По произведению на игрока. Вы ищете ретушь одного человека, одним-единственным щелчком. Самое простое правило в игре, и самое напряжённое.',
      },
      {
        name: 'Рисует один',
        text: 'Раунд, один рисующий, все остальные в погоне. Роль переходит по кругу, а рисующий смотрит, как его тайник падает или держится.',
      },
      {
        name: 'Королевская битва',
        text: 'Та же охота, но первая раскрытая ретушь выбивает своего автора. Играют до последнего оставшегося и не считают ни одного очка.',
      },
      {
        name: 'Конкурс',
        text: 'Никто не ищет. Все перерисовывают один и тот же участок одной картины, а потом каждый голосует за ретушь, которая ему больше нравится. Каждый полученный голос даёт одно очко.',
      },
    ],
    faq: [
      {
        question: 'Сколько нужно игроков?',
        answer:
          'От 2 до 10. Если вы один, свободное место займёт автоматический соперник, чтобы партия началась сразу. От четырёх человек общая охота раскрывается лучше всего.',
      },
      {
        question: 'Это бесплатно?',
        answer:
          'Да. Быстрая игра, общая охота и режим стрима бесплатны, без ограничения по времени и по числу партий. Остальные режимы пока тоже бесплатны.',
      },
      {
        question: 'Нужен ли аккаунт?',
        answer:
          'Нет. Пишете имя, щёлкаете, играете. Аккаунт лишь сохраняет имя и аватар от партии к партии: он остаётся необязательным.',
      },
      {
        question: 'Работает ли на телефоне?',
        answer:
          'Да, интерфейс подстраивается под касание: свёрнутая панель инструментов, лупа под пальцем, двойное касание для приближения. При этом мышью и на большом экране рисовать точнее.',
      },
      {
        question: 'Как пригласить друзей?',
        answer:
          'Создаёте комнату, копируете ссылку-приглашение и вставляете её куда хотите. Друзья попадают прямо за стол, без аккаунта и без установки.',
      },
      {
        question: 'Что такое «ретушь»?',
        answer:
          'Это то, что вы рисуете на стёртом участке. Если получилось, её принимают за кусок исходной картины и никто её не находит. Если не получилось, она бросается в глаза.',
      },
    ],
    headings: {
      intro: 'Игра в рисование, где нужно исчезнуть',
      steps: 'Как играть',
      modes: 'Шесть режимов игры',
      faq: 'Частые вопросы',
      more: 'Что дальше',
    },
    links: {
      rules: 'Прочитать полные правила и систему очков',
      modes: 'Сравнить режимы подробно',
    },
  },

  howToPlay: {
    title: 'Как играть в Retusch',
    summary:
      'Полные правила Retusch: ход раунда, начисление очков с обеих сторон, инструменты рисования и советы, как прятаться и как искать.',
    sections: [
      {
        heading: 'Идея в трёх фразах',
        blocks: [
          'Вам дают картину, с которой стёрли один участок, и вы перерисовываете его так, чтобы он прошёл незамеченным.',
          'Потом все смотрят на отретушированные картины и ищут перерисованные участки.',
          'Вы набираете очки, находя быстро, и набираете их всё то время, пока держится ваш тайник.',
        ],
      },
      {
        heading: 'Ход раунда',
        blocks: [
          'Партия связывает одинаковые раунды, по умолчанию пять. Каждый проходит через три момента.',
          {
            list: [
              'Рисование. Вы получаете свою картину и свой участок, и у вас по умолчанию 2 минуты, чтобы его перерисовать. Кнопка Готово означает сдачу: как только сдали все, раунд идёт дальше, не дожидаясь таймера.',
              'Охота. Картина появляется снова, уже с ретушами, и у вас по умолчанию 30 секунд, чтобы их заметить. Щелчок по перерисованному участку раскрывает его всему столу.',
              'Раскрытие. Все тайники обводятся рамкой и получают имя, в том числе те, которых никто не увидел. Ползунок позволяет сравнить отретушированную картину с исходным произведением.',
            ],
          },
        ],
      },
      {
        heading: 'Начисление очков',
        blocks: [
          'Два счётчика идут вместе и противостоят друг другу: тот, кто ищет, хочет успеть быстро; тот, кто рисовал, хочет, чтобы его участок продержался.',
          {
            list: [
              'Тому, кто ищет: 1000 очков, если нашли в первые 3 секунды, затем ровное снижение до 100 очков к концу фазы. Промах и ненайденное не дают ничего.',
              'Тому, кто рисует: всего 100 очков, если ваш участок падает сразу, затем ровный рост до 800 очков, если он держится до конца. Ретушь, которую никто не нашёл, стоит максимума, 800 очков.',
            ],
          },
          'Рисующему платят один раз, при раскрытии, и по самому быстрому искателю. Поэтому неважно, ищут вас двое или восемь: считается только тот, кто нашёл первым.',
          'Начисление одинаково, какую бы длительность фазы вы ни выставили. Удлинение охоты не сдвигает две крайние точки, меняется только наклон между ними.',
          'Одна-единственная ошибка за раунд. Первый щелчок, не попавший ни в одну ретушь, закрывает вашу охоту: без этого правила можно было бы прочёсывать изображение наугад, пока не наткнёшься. Два действия не стоят ничего: щелчок по собственному участку и щелчок по уже найденной ретуши.',
        ],
      },
      {
        heading: 'Инструменты рисования',
        blocks: [
          {
            list: [
              'Кисть (B): три быстрых размера плюс ползунок для точного размера. Сочетания 1, 2 и 3 выбирают эти три размера.',
              'Заливка (P): одним действием заполняет ровную поверхность и никогда не выходит за ваш участок.',
              'Пипетка (I): берёт цвет в любой точке картины. Удерживание Alt переключает на неё на один щелчок и затем возвращает прежний инструмент.',
              'Рука (H): двигает вид. За пределами вашего участка кисть и заливка делают то же самое, так что менять инструмент почти никогда не нужно.',
              'Отмена (Ctrl+Z или Cmd+Z): возвращает по одному штриху, до тридцати раз. Повтора нет.',
              'Цвет фона в конце палитры: это начальный цвет вашего участка. Рисовать им значит стирать, с той разницей, что это такой же штрих, как все прочие, который можно отменить и перекрыть. Поэтому в игре нет ластика.',
            ],
          },
        ],
      },
      {
        heading: 'Пять советов, как спрятаться',
        blocks: [
          {
            list: [
              'Берите цвета пипеткой, не угадывайте их. Пипетка даёт точный цвет окружения: чуть промахнувшаяся заливка видна издалека, а точная становится невидимой.',
              'Продолжайте то, что пересекает ваш участок. Стена, горизонт, ветка, вошедшая с одной стороны, должна выйти с другой ровно на своём месте. Прерванная линия это первая улика, которую ищет опытный игрок.',
              'Красьте сначала углы. Это самая частая ошибка: центр выписан, четыре угла остались на исходном фоне, и квадрат виден невооружённым глазом, даже без взгляда на рисунок.',
              'Идите от общего к детали. Сначала положите крупные цветовые массы толстой кистью, потом уточняйте. В обратном порядке всё время уходит на один угол, а остальное остаётся пустым.',
              'Повторяйте степень детализации окружения. Слишком чистый участок посреди насыщенной области виден так же, как небрежный посреди ровной заливки.',
            ],
          },
        ],
      },
      {
        heading: 'Четыре совета, как искать',
        blocks: [
          {
            list: [
              'Ищите линии, которые прерываются. Кромка, провод, угол стола, сдвинутый на волосок: почти всегда это там.',
              'Сначала просмотр, потом приближение. В общем виде плохо слитый участок выглядит пятном. Приближение нужно для подтверждения, а не для поиска.',
              'Не доверяйте слишком гладкому. Идеально ровная поверхность посреди текстуры так же подозрительна, как небрежное пятно.',
              'Не щёлкайте наугад. У вас право на одну-единственную ошибку: лучше потерять три секунды на проверку, чем весь раунд.',
            ],
          },
        ],
      },
      {
        heading: 'А в зависимости от режима?',
        blocks: [
          'Всё сказанное выше описывает общую охоту, режим по умолчанию. Остальные режимы меняют, кто рисует, кто ищет и как побеждают: подробности каждого на странице режимов игры.',
        ],
      },
    ],
  },

  modes: {
    title: 'Режимы игры Retusch',
    summary:
      'Шесть способов играть в Retusch: общая охота, стрим, у каждого своя картина, рисует один, королевская битва и конкурс. Ход, рекомендуемое число игроков и длительность.',
    sections: [
      {
        heading: 'Общая охота',
        blocks: [
          'Режим по умолчанию, и тот, с которого стоит начать.',
          'Все рисуют на одной и той же картине, каждый на своём участке. Когда ретуши сданы, произведение возвращается целиком и охота открывается сразу для всех. Ретушь находят только один раз, и это делает тот, кто заметил её первым: она появляется для всего стола и выходит из игры. Свою вы не найдёте никогда, а тайник, которого никто не увидел, даёт автору максимум.',
          'Идеально для большой компании: нет мёртвых пауз, все ищут одновременно, и каждая находка появляется на экране у всех.',
          'Рекомендуется: от 3 до 10 игроков.',
        ],
      },
      {
        heading: 'Стрим',
        blocks: [
          'Зеркальное отражение режима «рисует один», придуманное для прямого эфира.',
          'Все игроки рисуют на одной и той же картине, кроме хозяина комнаты. Потом хозяин ищет один, а его зрители смотрят, как он мучается. Он не получает очков и не попадает в таблицу: партия идёт между теми, кто прячется, а он ведёт охоту. Он может ошибаться сколько угодно, правило единственной ошибки на него не распространяется.',
          'Идеально для вечера в прямом эфире, и по вполне определённой причине: раз хозяин не рисует, зрители никогда не видят, как строится его тайник. В остальных режимах они наблюдают, как он красит свой участок, и охота не хранит для них никакой тайны.',
          'Рекомендуется: от 4 до 10 игроков. Бесплатно, и таким останется.',
        ],
      },
      {
        heading: 'У каждого своя картина',
        blocks: [
          'Каждый игрок получает своё произведение и прячет в нём свою ретушь. Во время охоты вам дают картину только одного другого человека, и у вас один щелчок, чтобы указать перерисованный участок. Никакой гонки, никакой конкуренции: только вы, картина и одно решение.',
          'Идеально вдвоём, или когда хочется спокойной партии, где каждый не торопится.',
          'Рекомендуется: от 2 до 10 игроков.',
        ],
      },
      {
        heading: 'Рисует один',
        blocks: [
          'Раунд, один рисующий, все остальные в погоне.',
          'В каждом раунде картину получает один человек. Остальные ждут, а потом ищут его ретушь, и каждого оценивают по его собственному времени. Рисующего оценивают по самому быстрому искателю, и он снова видит свой тайник, застывший, пока его ищут. Роль переходит по кругу: здесь настройка раундов считает полные круги, ведь партия честна только тогда, когда каждый рисовал одинаковое число раз.',
          'Идеально для небольшой компании, которая хочет померяться силами в равных условиях, рисунок за рисунком.',
          'Рекомендуется: от 3 до 6 игроков, выше ожидание становится долгим.',
        ],
      },
      {
        heading: 'Королевская битва',
        blocks: [
          'Общая охота с одним выбыванием за раунд.',
          'Та же общая картина, та же одновременная охота. Но здесь не считают ни одного очка: единственный вопрос это кто останется. Игрок, чью ретушь раскрыли первой, покидает партию. Остальные продолжают искать до конца раунда, и это уже никого не выбивает, зато даёт каждому своё место, показанное на его участке. Если не нашли никого, не выбывает никто.',
          'Идеально, чтобы закрыть вечер: напряжение растёт по мере того, как стол пустеет, а последний раунд играют вдвоём.',
          'Рекомендуется: от 4 до 10 игроков.',
        ],
      },
      {
        heading: 'Конкурс',
        blocks: [
          'Единственный режим, где никто не ищет.',
          'Все перерисовывают один и тот же участок одной картины. Потом каждый в своём темпе просматривает ретуши остальных и голосует за ту, которая кажется ему самой удачной. Каждый полученный голос даёт автору одно очко. Во время голосования авторы анонимны, и голосовать за себя нельзя: имена и голоса выходят наружу только при раскрытии.',
          'Идеально, когда компания разношёрстная, или когда кто-то предпочитает рисовать без давления охотничьего таймера.',
          'Рекомендуется: от 3 до 10 игроков.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Общая охота', players: 'от 3 до 10', duration: '15-20 мин' },
    { name: 'Стрим', players: 'от 4 до 10', duration: '15-20 мин' },
    { name: 'У каждого своя картина', players: 'от 2 до 10', duration: '15-20 мин' },
    { name: 'Рисует один', players: 'от 3 до 6', duration: '20-35 мин' },
    { name: 'Королевская битва', players: 'от 4 до 10', duration: '20-30 мин' },
    { name: 'Конкурс', players: 'от 3 до 10', duration: '10-15 мин' },
  ],
  modeTableHeadings: ['Режим', 'Игроки', 'Длительность'],
  premium: {
    heading: 'Бесплатные и платные режимы',
    blocks: [
      'Общая охота и режим стрима бесплатны, окончательно.',
      'Остальные четыре станут платными при СОЗДАНИИ партии, и только там. Присоединяться всегда останется бесплатно: достаточно, чтобы игру разблокировал один человек за столом, и все смогут играть во все режимы, без аккаунта и без оплаты.',
      'Разблокировка будет разовой покупкой, без подписки, и заодно уберёт рекламу для того аккаунта, который её совершил. А пока все режимы открыты всем.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Что такое Retusch?',
      date: '2026-08-21',
      summary:
        'Онлайн-игра в рисование для нескольких человек, по форме близкая к Gartic Phone, но её механика напоминает скорее Meccha Chameleon: вместо того чтобы дать себя узнать, вы пытаетесь исчезнуть.',
      sections: [
        {
          heading: 'Игра в рисование, где быть узнанным значит проиграть',
          blocks: [
            'Retusch это онлайн-игра в рисование для 2-10 игроков. Каждый раунд вам достаётся иллюстрация, с которой стёрли один участок, и его нужно перерисовать так, чтобы он слился с произведением. Потом все смотрят на отретушированные картины и ищут перерисованные участки.',
            'То, что отличает её, умещается в одну фразу: побеждает тот, кого не заметили.',
          ],
        },
        {
          heading: 'Что у неё общего с Gartic Phone',
          blocks: [
            'Форма та же: открываете ссылку, пишете имя, играете. Никакого аккаунта, никакой установки, никакого клиента для загрузки. Игра работает в браузере, и партия начинается за несколько секунд. Друзей приглашают, вставив ссылку в разговор, и стол наполняется.',
            'Ритм тоже знакомый: короткие раунды, таймер, фаза рисования, а затем фаза, где выясняется, что наделали остальные. И главное, то самое общее раскрытие в конце раунда, когда все обсуждают увиденное. Это то же самое совместное удовольствие.',
            'Как и в Gartic Phone, уметь рисовать не нужно. Хорошо положенный кусок неба бьёт шедевр, который вылезает за края.',
          ],
        },
        {
          heading: 'Что она берёт у Meccha Chameleon',
          blocks: [
            'В глубине, однако, механика в другом. В Meccha Chameleon, трёхмерной игре в Steam, вы раскрашиваете собственного персонажа, а потом помещаете его в декорацию в надежде, что никто не отличит его от окружения. Победа приходит из сходства и удачного размещения, а не из действия.',
            'Retusch берёт эту идею и возвращает её к изображению. Вы не выбираете, куда спрятаться, участок вам даёт игра; зато всё, что окажется внутри, вы изготавливаете целиком сами. Тайник удался, если взгляд скользит по нему не останавливаясь.',
            'И как в любой игре про мимикрию, напряжение рождается из двойной роли. Вы одновременно и прячетесь, и охотитесь: пока вы всматриваетесь в картину в поисках подозрительной заливки, кто-то всматривается в вашу.',
          ],
        },
        {
          heading: 'Чего нет больше нигде',
          blocks: [
            'Начисление очков награждает две стороны противоположным образом. Тот, кто ищет, получает тем больше, чем быстрее нашёл; тот, кто рисовал, получает тем больше, чем дольше держится его участок. Ретушь, которой никто не увидел, даёт автору максимум.',
            'Отсюда рождается очень своеобразная манера игры: вы стремитесь не к красивому, а к точному. Взять верный цвет, продолжить линию ровно там, где нужно, не оставить ни одного пустого угла. Это движения наблюдения прежде, чем движения рисования.',
            'Чтобы понять это на практике, проще всего прочитать полные правила или запустить быструю игру: она стартует без настроек и без ожидания.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Как стать непобедимым в Retusch',
      date: '2026-08-21',
      summary:
        'Сочетания клавиш, метод от общего к детали и ошибка пустых углов: три вещи, которые отделяют новичка от игрока, которого больше не находят.',
      sections: [
        {
          heading: 'Выучите сочетания клавиш и удвоите полезное время',
          blocks: [
            'Две минуты рисования пролетают очень быстро, если за каждым инструментом ходить мышью. Сочетания клавиш легко возвращают двадцать или тридцать секунд за раунд, и это ровно те секунды, которые уйдут на детали.',
            {
              list: [
                'B для кисти, P для заливки, I для пипетки, H для руки.',
                '1, 2 и 3 для трёх размеров кисти, не заходя в панель инструментов.',
                'Ctrl+Z, на Mac Cmd+Z, чтобы отменять по одному штриху.',
                'Удерживание Alt переключает на пипетку и возвращает прежний инструмент, как только вы её отпустите. Это самое полезное из всего: вы берёте цвет прямо по ходу дела, ни разу не меняя инструмент.',
              ],
            },
            'Замечание для клавиатур AZERTY: клавиши 1, 2 и 3 работают без Shift, даже если на вашей клавиатуре на них написано &, é и ". Игра читает положение клавиши, а не символ, который она выдаёт.',
          ],
        },
        {
          heading: 'Красьте от общего к детали',
          blocks: [
            'Классическая ошибка новичка это начать с угла и вылизывать его. Таймер кончается, три четверти участка всё ещё фон, и ретушь видно за десять метров.',
            'Верный метод обратный. Начните толстой кистью или заливкой и положите крупные цветовые массы: небо, стену, тень на земле. За двадцать секунд ваш участок уже даёт верное общее впечатление и больше не читается чужеродным блоком.',
            'Только после этого уменьшайте размер и занимайтесь переходами: линией горизонта, которая пересекает участок, кромкой предмета, градиентом, меняющимся на середине высоты. Если времени не хватит, вы потеряете деталь, а не раунд.',
            'Цвета всегда берите пипеткой, а не выбирайте на палитре. Приблизительного оттенка хватает, чтобы выдать целый участок, тогда как точный оттенок делает его невидимым.',
          ],
        },
        {
          heading: 'Никогда не оставляйте пустые углы',
          blocks: [
            'Это улика номер один и первое, что ищут опытные игроки. Участок с хорошо проработанной серединой, но с четырьмя углами, оставшимися на цвете фона, рисует на экране идеальный прямоугольник. На рисунок даже смотреть не нужно: геометрическая фигура бросается в глаза.',
            'Возьмите за привычку прорабатывать сначала край, и только потом середину. Хорошо слитая рамка простит приблизительную внутренность; обратное не верно никогда.',
            'Заходите за края нарочно. Игра обрезает ваш штрих на границе участка, так что вы ничем не рискуете, уходя намного дальше, и это единственный способ убедиться, что ни один пиксель фона не уцелел в углу.',
          ],
        },
        {
          heading: 'А когда очередь искать доходит до вас',
          blocks: [
            'Сначала просмотрите картину целиком без приближения: плохо слитый участок выглядит пятном, а приближение годится только для подтверждения. Потом идите вдоль длинных линий, тех, что пересекают изображение: кромка, сдвинутая на волосок, почти всегда ретушь.',
            'И помните, что у вас право на одну-единственную ошибку за раунд. Три секунды проверки стоят дешевле, чем целый раунд.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Игра в рисование для вечера с друзьями',
      date: '2026-08-21',
      summary:
        'Почему Retusch хорошо работает в небольшой компании, как приглашать, вставив ссылку, и почему достаточно одного игрока, чтобы открыть платные режимы всему столу.',
      sections: [
        {
          heading: 'Игра, которая ни от кого ничего не требует',
          blocks: [
            'Главное препятствие игрового вечера на расстоянии это установка. Аккаунт, который надо завести, клиент, который надо скачать, версия, которая не совпадает: половина компании сдаётся ещё до игры.',
            'Retusch работает в браузере. Друзья щёлкают по ссылке, пишут имя и оказываются за столом. Устанавливать нечего, заводить нечего, и всё это одинаково хорошо работает и на компьютере, и на телефоне.',
            'Ещё одна важная в компании вещь: никто не остаётся за бортом из-за того, что не умеет рисовать. Игра не просит вас что-то изобразить, только подогнать участок под то, что его окружает. Тот, кто рисует плохо, но наблюдает хорошо, побеждает часто.',
          ],
        },
        {
          heading: 'Приглашение на практике',
          blocks: [
            'Создайте комнату с начального экрана. Снимите отметку «публичная комната», если хотите остаться в своём кругу: приглашение по ссылке станет тогда единственным входом.',
            'Скопируйте ссылку соответствующей кнопкой и вставьте её в ваш групповой чат, в ваш голосовой канал или в ваш мессенджер. В ней есть код комнаты: друзья приземляются прямо за ваш стол.',
            'Партию можно настраивать, пока собираются люди: режим, число раундов, время на рисование, размер участка и даже категории изображений. Ничто не закреплено, пока вы не запустите.',
          ],
        },
        {
          heading: 'Один платит, играют все',
          blocks: [
            'Два режима бесплатны навсегда: общая охота, то есть режим по умолчанию, и режим стрима.',
            'Остальные станут платными, но только при СОЗДАНИИ партии. Присоединяться останется бесплатно, без всяких условий. На практике достаточно, чтобы игру разблокировал один человек из компании, и весь стол получит все режимы, без аккаунта и без единой оплаты.',
            'Разблокировка это разовая покупка, без подписки.',
          ],
        },
        {
          heading: 'Какой режим для какой компании',
          blocks: [
            'Втроём или вчетвером общей охоты хватает, и она понятна за один раунд. От шести человек она становится по-настоящему хороша: находки идут одна за другой, и экран не перестаёт двигаться.',
            'Чтобы закрыть вечер, попробуйте королевскую битву: стол пустеет раунд за раундом, а последний играют вдвоём. Для разношёрстной компании, где кто-то предпочитает рисовать спокойно, конкурс снимает всё давление охотничьего таймера.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Игра в рисование для прямого эфира',
      date: '2026-08-21',
      summary:
        'У Retusch есть режим, придуманный для прямого эфира, в котором стример ищет один, пока его зрители прячутся. Как им пользоваться и какие ещё режимы работают перед публикой.',
      sections: [
        {
          heading: 'Режим, сделанный специально',
          blocks: [
            'Режим стрима переворачивает роли. Все игроки рисуют на одной и той же картине, кроме хозяина комнаты, который не рисует вовсе. Потом хозяин ищет один, а его зрители смотрят.',
            'Это ровно та ситуация, которой требует прямой эфир, и по вполне определённой причине: раз хозяин не рисует, его тайника не существует, и чат не увидел ничего, чего видеть не должен был. Зрители зато знают тайники остальных игроков и могут смотреть, как он трижды подряд проходит мимо.',
            'Для этого случая написаны два правила. Стример не получает очков и не попадает в таблицу: партия идёт между теми, кто прячется, а он ведёт зрелище. И он может ошибаться сколько угодно, тогда как все остальные режимы останавливаются на первой ошибке. Прямой эфир не может закончиться на неудачном щелчке.',
            'Режим стрима бесплатен, и таким останется.',
          ],
        },
        {
          heading: 'Конкурс, вторая хорошая идея',
          blocks: [
            'В режиме конкурса все перерисовывают один и тот же участок одной картины, а потом каждый голосует за ретушь, которая ему больше нравится. Находить больше нечего, остаётся только судить.',
            'Перед публикой это работает отлично, и по той же причине, что и режим стрима: находить нечего, значит, чату нечего испортить. Работы сравнивают бок о бок, голосование даёт момент напряжения, а раскрытие имён предлагает чёткую точку. Это заодно и самый короткий режим.',
          ],
        },
        {
          heading: 'И остальные режимы',
          blocks: [
            'Ничто не обязывает брать специальный режим, но тогда лучше играть с друзьями, а не с чатом. Во всех остальных режимах стример красит свой участок на экране: зрители видят, как он строится, и знают ответ ещё до начала охоты.',
            'Партия между друзьями, напротив, прекрасно смотрится со стороны: фаза рисования показывает, как каждый строит свой тайник, а раскрытие вскрывает все участки разом, с именем автора.',
          ],
        },
        {
          heading: 'Как втянуть чат в партию',
          blocks: [
            'Ссылка-приглашение это единственное, чем нужно поделиться. Вставьте её в чат, и первые пришедшие заполнят стол: никакого аккаунта, никакой загрузки, никакой очереди.',
            'Предел числа игроков настраивается в комнате, от 2 до 10. При многочисленной публике открывайте несколько коротких партий вместо одной длинной: раунд длится меньше трёх минут.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Что нового в Retusch',
      date: '2026-08-21',
      summary:
        'Игровые изменения в Retusch, от самого свежего к самому старому: новые режимы, новые правила, улучшения в рисовании и в охоте.',
      sections: [
        {
          heading: 'Август 2026',
          blocks: [
            {
              list: [
                'Участок для перерисовки больше не начинается с белого квадрата, а с преобладающего цвета того, что он закрывает. Половина времени больше не уходит на перекрашивание фона, которого на картине не существует.',
                'Заливка больше не оставляет светлую кайму вдоль штрихов: теперь она подтекает под их смягчённый край.',
                'Королевская битва больше не считает очки. Первый раскрытый игрок покидает партию, а каждый получает место в раунде, показанное на его участке, от красного к зелёному.',
                'Режим стрима даёт стримеру столько попыток, сколько он захочет: первая ошибка больше не закрывает раунд.',
                'В режиме зрителя теперь можно двигать и приближать картину, за созданием которой вы наблюдаете.',
                'Эмодзи-реакции вылетают от имени их автора в списке игроков, а не поднимаются снизу экрана.',
                'За пределами участка для перерисовки кисть и заливка двигают вид: чтобы переместиться, больше не нужно брать инструмент рука.',
                'Название игры слева вверху во время партии возвращает на начальную страницу после подтверждения.',
                'Японский и корейский добавляются к пятнадцати уже доступным языкам.',
              ],
            },
          ],
        },
        {
          heading: 'Июль 2026',
          blocks: [
            {
              list: [
                'Новый режим конкурса: все перерисовывают один и тот же участок, а потом каждый голосует за ретушь, которая ему больше нравится.',
                'Новый режим стрима, придуманный для прямого эфира: хозяин комнаты ищет один, пока его зрители прячутся.',
                'Категории изображений заменяют уровни сложности. Вы отмечаете то, что хотите видеть, и каждая категория показывает картину-образец.',
                'Два раунда подряд больше не выпадают на одну и ту же картину, а отмеченные категории чередуются от раунда к раунду.',
                'Галерея в конце партии позволяет пересмотреть ретушь штрих за штрихом и выгрузить её как изображение или как анимированный GIF.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'Коротко',
    allArticles: 'Все статьи',
    theRules: 'Правила игры',
    theModes: 'Режимы игры',
  },
};
